import {
  type QueryFunctionContext,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useCompany } from '@/contexts/CompanyContext';
import { useMode } from '@/providers/ModeProvider';

/**
 * Options for useModeAwareQuery
 */
interface ModeAwareQueryOptions<TData, TError> extends Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'> {
  /**
   * When true, always use live mode (call API) regardless of global mode setting.
   * Use this for screens that should always fetch from the backend.
   */
  forceLive?: boolean;
  /**
   * When true, the query fires even when no company is selected.
   * Use only for endpoints that are excluded from X-Company-Id validation
   * (e.g. /api/v1/currencies, /api/v1/currencies/erp).
   */
  skipCompanyCheck?: boolean;
}

/**
 * Custom hook that wraps useQuery and respects the Demo/Live mode
 *
 * @param queryKey - The query key for caching
 * @param liveFn - Function to fetch live data from backend (receives QueryFunctionContext with signal for cancellation)
 * @param demoData - Mock data to use in demo mode
 * @param options - Additional query options (including forceLive to always use API)
 * @returns UseQueryResult with data based on current mode
 *
 * @example
 * const { apiGet } = useApi();
 * const { data, isLoading } = useModeAwareQuery(
 *   ['customers'],
 *   async ({ signal }) => {
 *     return apiGet<CustomersApiResponse>('/api/v1/customers', undefined, signal);
 *   },
 *   mockCustomers,
 *   { staleTime: 5 * 60 * 1000, forceLive: true }
 * );
 */
export function useModeAwareQuery<TData = unknown, TError = Error>(
  queryKey: unknown[],
  liveFn: (ctx: QueryFunctionContext) => Promise<TData>,
  demoData: TData,
  options?: ModeAwareQueryOptions<TData, TError>,
): UseQueryResult<TData, TError> {
  const { isDemo } = useMode();
  const { selectedCompany } = useCompany();

  // If forceLive is true, always use live mode
  const { forceLive, skipCompanyCheck, ...queryOptions } = options ?? {};
  const effectiveIsDemo = forceLive ? false : isDemo;

  // In live mode, prevent queries from firing until a company is selected.
  // This avoids API calls without the X-Company-Id header which the backend
  // requires for all non-excluded endpoints.
  const companyReady = effectiveIsDemo || !!skipCompanyCheck || !!selectedCompany?.id;
  const callerEnabled = queryOptions.enabled ?? true;

  return useQuery<TData, TError>({
    queryKey: [...queryKey, effectiveIsDemo ? 'demo' : 'live', selectedCompany?.id ?? 'no-company'],
    queryFn: async (ctx) => {
      if (effectiveIsDemo) {
        // Simulate network delay in demo mode for realistic UX
        // Make the delay cancellable as well
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(resolve, 300);
          if (ctx.signal) {
            ctx.signal.addEventListener('abort', () => {
              clearTimeout(timeout);
              // Match fetch's behaviour: reject with an AbortError-like object
              reject(new DOMException('Aborted', 'AbortError'));
            });
          }
        });
        return demoData;
      }
      return liveFn(ctx);
    },
    ...queryOptions,
    enabled: callerEnabled && companyReady,
  });
}

/**
 * Hook to get data synchronously based on mode
 * Useful for components that don't need async loading states
 *
 * @param liveData - Data from live backend
 * @param demoData - Mock data for demo mode
 * @returns Data based on current mode
 */
export function useModeAwareData<TData>(liveData: TData, demoData: TData): TData {
  const { isDemo } = useMode();
  return isDemo ? demoData : liveData;
}

/**
 * Options for useModeAwareMutation
 */
interface ModeAwareMutationOptions<TData, TError, TVariables>
  extends Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'> {
  /**
   * When true, always use live mode (call API) regardless of global mode setting.
   * Use this for screens that should always call the backend.
   */
  forceLive?: boolean;
}

/**
 * Custom hook that wraps useMutation and respects the Demo/Live mode
 *
 * @param liveFn - Function to call in live mode (actual API call)
 * @param demoFn - Function to call in demo mode (mock response)
 * @param options - Additional mutation options (including forceLive to always use API)
 * @returns UseMutationResult based on current mode
 *
 * @example
 * const { apiPost } = useApi();
 * const createUser = useModeAwareMutation(
 *   async (data) => apiPost('/api/v1/users', data),
 *   async (data) => ({ id: Date.now(), ...data }), // mock response
 *   { onSuccess: () => queryClient.invalidateQueries(['users']), forceLive: true }
 * );
 */
export function useModeAwareMutation<TData = unknown, TError = Error, TVariables = void>(
  liveFn: (variables: TVariables) => Promise<TData>,
  demoFn: (variables: TVariables) => TData | Promise<TData>,
  options?: ModeAwareMutationOptions<TData, TError, TVariables>,
): UseMutationResult<TData, TError, TVariables> {
  const { isDemo } = useMode();

  // If forceLive is true, always use live mode
  const { forceLive, ...mutationOptions } = options ?? {};
  const effectiveIsDemo = forceLive ? false : isDemo;

  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables) => {
      if (effectiveIsDemo) {
        // Simulate network delay in demo mode
        await new Promise((resolve) => setTimeout(resolve, 300));
        return demoFn(variables);
      }
      return liveFn(variables);
    },
    ...mutationOptions,
  });
}
